import { supabase } from './supabase';

const BUCKET_NAME = 'security-reports';

export const storage = {
  /**
   * Upload a file to the security reports bucket
   * @param file - The file to upload
   * @param path - The path within the bucket (e.g., 'Africa/Nigeria/Lagos/report.pdf')
   * @returns Object with publicUrl or error
   */
  uploadReport: async (file: File, path: string) => {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false, // Don't overwrite existing files
        });

      if (error) throw error;

      // Get the public URL for the file
      const { data: urlData } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(path);

      return {
        data: {
          path: data.path,
          publicUrl: urlData.publicUrl,
        },
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  },

  /**
   * Get a signed URL for downloading a file (valid for 1 hour)
   * @param path - The path to the file
   * @returns Signed URL or error
   */
  getSignedUrl: async (path: string) => {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .createSignedUrl(path, 3600); // 1 hour expiry

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get URL',
      };
    }
  },

  /**
   * Download a file directly
   * @param path - The path to the file
   * @returns File blob or error
   */
  downloadReport: async (path: string) => {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .download(path);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Download failed',
      };
    }
  },

  /**
   * List all files in a specific folder
   * @param folderPath - The folder path (e.g., 'Africa/Nigeria/Lagos')
   * @returns List of files or error
   */
  listReports: async (folderPath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .list(folderPath, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' },
        });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'List failed',
      };
    }
  },

  /**
   * Delete a file
   * @param path - The path to the file
   * @returns Success or error
   */
  deleteReport: async (path: string) => {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .remove([path]);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Delete failed',
      };
    }
  },

  /**
   * Get public URL for a file (works only if bucket is public)
   * For private buckets, use getSignedUrl instead
   * @param path - The path to the file
   * @returns Public URL
   */
  getPublicUrl: (path: string) => {
    const { data } = supabase.storage.from(BUCKET_NAME).getPublicUrl(path);
    return data.publicUrl;
  },
};
